import React, { useEffect, useState } from 'react';
import { Loader2, Link2, RefreshCw, CheckCircle2, Calendar, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import NewsletterProgressBlock, { consumeSSE } from './NewsletterProgressBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MARKET_LABELS = {
  SE: { name: 'Sverige',        flag: '🇸🇪' },
  NO: { name: 'Norge',          flag: '🇳🇴' },
  FI: { name: 'Finland',        flag: '🇫🇮' },
  DK: { name: 'Danmark',        flag: '🇩🇰' },
  DE: { name: 'Tyskland',       flag: '🇩🇪' },
  NL: { name: 'Nederländerna',  flag: '🇳🇱' },
  UNKNOWN: { name: 'Okänd marknad', flag: '❓' },
};

function formatDate(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function NewsletterImportTab() {
  const { session, user, isAdmin } = useAuth();
  const accessToken = session?.access_token;

  const [mcLoading, setMcLoading] = useState(false);
  const [mcProgress, setMcProgress] = useState(null);
  const [mcLog, setMcLog] = useState([]);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const loadStatus = async () => {
    if (!accessToken) { setStatusLoading(false); return; }
    setStatusLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/sync-status`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || r.status);
      setStatus(await r.json());
    } catch (e) {
      setStatus({ total: 0, by_market: {}, synced_at: null });
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [accessToken]);

  const handleMailchimp = async () => {
    setMcLoading(true);
    setMcLog([]);
    setMcProgress(null);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/mailchimp-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ user_id: user.id, access_token: accessToken }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      await consumeSSE(r, (ev) => {
        if (ev.type === 'status' && ev.message) setMcLog(l => [...l, ev.message]);
        if (ev.type === 'progress') setMcProgress(ev);
        if (ev.type === 'error') {
          setMcLog(l => [...l, `Fel: ${ev.message}`]);
          toast.error(ev.message);
        }
        if (ev.type === 'done') {
          setMcProgress(ev);
          setMcLog(l => [...l, ev.message]);
          toast.success(ev.message);
        }
      });
      await loadStatus();
    } catch (e) {
      toast.error(`Synk misslyckades: ${e.message}`);
    } finally {
      setMcLoading(false);
    }
  };

  return (
    <div data-testid="nl-import-tab" style={{ display: 'grid', gap: 24 }}>
      {/* ── Sync status summary ────────────────────────────────────────── */}
      {statusLoading ? (
        <section className="dash-card" style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)' }}>
          <Loader2 size={14} className="ap-progress-spin" /> Laddar status...
        </section>
      ) : status && status.total > 0 ? (
        <section className="dash-card" style={{ padding: 24 }} data-testid="nl-sync-status">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <CheckCircle2 size={20} style={{ color: '#22c55e' }} />
            <h2 className="dash-card-title" style={{ margin: 0 }}>
              {status.total.toLocaleString('sv-SE')} kampanjer synkade från Mailchimp
            </h2>
          </div>

          {status.synced_at && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
              <Calendar size={12} />
              Senast synkad: {formatDate(status.synced_at)}
              {status.dc && <span style={{ marginLeft: 8 }}>(dc: {status.dc})</span>}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {Object.entries(status.by_market || {})
              .sort((a, b) => b[1] - a[1])
              .map(([mkt, count]) => {
                const m = MARKET_LABELS[mkt] || MARKET_LABELS.UNKNOWN;
                return (
                  <div
                    key={mkt}
                    data-testid={`nl-market-count-${mkt}`}
                    style={{
                      padding: '10px 16px', borderRadius: 10,
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 10, minWidth: 130,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{m.flag}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{m.name}</span>
                      <strong style={{ fontSize: 15, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                        {count.toLocaleString('sv-SE')}
                      </strong>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </section>
      ) : null}

      {/* ── Mailchimp sync trigger (admin only) ────────────────────────── */}
      <section className="dash-card" style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link2 size={20} style={{ color: 'var(--accent)' }} />
            <h2 className="dash-card-title" style={{ margin: 0 }}>
              {status?.total > 0 ? 'Synka om från Mailchimp' : 'Synka från Mailchimp'}
            </h2>
          </div>
          {status?.total > 0 && (
            <button
              onClick={loadStatus}
              className="ap-queue-refresh"
              title="Uppdatera status"
              data-testid="nl-status-refresh"
            >
              <RefreshCw size={14} />
            </button>
          )}
        </div>

        {isAdmin ? (
          <>
            <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
              Hämtar alla skickade kampanjer från Mailchimp (~30s för 1500+ kampanjer)
              plus prestanda (öppningsfrekvens, klickfrekvens). API-nyckeln läses från
              backend-miljövariablerna. Re-sync uppdaterar befintliga rader. Datan delas
              mellan alla användare.
            </p>
            <button
              onClick={handleMailchimp}
              disabled={mcLoading}
              data-testid="nl-mc-sync"
              className="primary-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 24px', fontSize: 14, fontWeight: 600 }}
            >
              {mcLoading
                ? <><Loader2 size={14} className="ap-progress-spin" /> Synkar...</>
                : <>Synka från Mailchimp</>}
            </button>
          </>
        ) : (
          <div
            data-testid="nl-mc-admin-only"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: 14, borderRadius: 10,
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              fontSize: 13, color: 'var(--text)',
            }}
          >
            <ShieldAlert size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
            Endast administratörer kan trigga sync. Kampanjdatan delas mellan alla användare.
          </div>
        )}

        <NewsletterProgressBlock progress={mcProgress} log={mcLog} doneKey="synced" />
      </section>
    </div>
  );
}
